import { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { SiteLayout } from "@/components/site/SiteLayout";
import { StructuredData } from "@/components/StructuredData";
import { supabase } from "@/integrations/supabase/client";

interface SiteFaqRecord {
  id: string;
  group_title: string;
  question: string;
  answer: string;
  sort_order: number;
  published: boolean;
}

const fallbackGroups = [
  {
    title: "Merchant Benefits",
    items: [
      {
        q: "How does Splitkart actually grow my AOV?",
        a: "When customers can share the cost with friends, they're far more likely to add the premium variant or upgrade. Splitkart helps merchants unlock larger carts by reducing price shock at the moment of purchase.",
      },
      {
        q: "Are split orders a zero-cost referral channel?",
        a: "Yes. Every split-link your customer shares is your brand landing on a new shopper's screen. Participants discover the store while contributing to the order.",
      },
      {
        q: "Do I get paid upfront?",
        a: "You receive the full payout once all friends have contributed within the split window. Splitkart handles the coordination so merchants fulfill only when the order is fully paid.",
      },
    ],
  },
  {
    title: "Customer Experience",
    items: [
      {
        q: "Do my customer's friends need to create an account?",
        a: "No. Friends pay as guests using the secure contribution flow. The experience is designed to be lightweight, mobile-friendly, and fast.",
      },
      {
        q: "What happens if a friend doesn't pay?",
        a: "Splitkart uses a configurable split expiry window, typically 24–48 hours. Before the split expires, the organizer can choose to pay the remaining amount personally or share the contribution slot with another friend. If the split still does not complete, it follows the merchant's policy for incomplete payments.",
      },
      {
        q: "Is there interest, debt, or a credit check?",
        a: "Never. Splitkart is shared math, not financing. There are no loans, no debt obligations, and no credit checks.",
      },
    ],
  },
  {
    title: "Technical",
    items: [
      {
        q: "Is Splitkart compatible with Shopify?",
        a: "Yes. Splitkart is built for modern Shopify storefronts and aligned with Checkout Extensibility for the app experience.",
      },
      {
        q: "How fast does the contributor page load?",
        a: "The contributor flow is designed to stay extremely lightweight and mobile-friendly so shared payment links feel immediate when they open.",
      },
      {
        q: "Can I customize the look?",
        a: "The split flow is branded around the merchant experience using the product, store, and checkout context that customers already trust.",
      },
    ],
  },
];

function FAQ() {
  const [faqRecords, setFaqRecords] = useState<SiteFaqRecord[]>([]);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const { data, error } = await supabase
          .from("site_faqs")
          .select("*")
          .eq("published", true)
          .order("group_title", { ascending: true })
          .order("sort_order", { ascending: true });

        if (error) throw error;
        setFaqRecords((data || []) as SiteFaqRecord[]);
      } catch (error) {
        console.error("Failed to load FAQs", error);
      }
    };

    loadFaqs();
  }, []);

  const groups = useMemo(() => {
    if (faqRecords.length === 0) return fallbackGroups;

    const grouped = new Map<string, Array<{ q: string; a: string }>>();
    faqRecords.forEach((faq) => {
      const key = faq.group_title || "General";
      const existing = grouped.get(key) || [];
      existing.push({ q: faq.question, a: faq.answer });
      grouped.set(key, existing);
    });

    return Array.from(grouped.entries()).map(([title, items]) => ({ title, items }));
  }, [faqRecords]);

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: groups.flatMap((group) =>
        group.items.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
          },
        }))
      ),
    }),
    [groups]
  );

  return (
    <SiteLayout>
      <SEO
        title="FAQ"
        description="Frequently asked questions about Splitkart, from merchant ROI and referrals to customer experience and platform compatibility."
        url="/faq"
      />
      <StructuredData data={faqSchema} />

      <section className="section bg-gradient-hero">
        <div className="container-tight text-center">
          <span className="eyebrow">FAQ</span>
          <h1 className="mt-5 text-balance text-4xl font-bold md:text-6xl">Everything you wanted to ask.</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Clear answers for merchants, organizers, and contributors exploring Splitkart.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-tight max-w-3xl space-y-12">
          {groups.map((group) => (
            <div key={group.title}>
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary">{group.title}</h2>
              <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card shadow-soft">
                {group.items.map((item, idx) => (
                  <AccordionItem key={item.q} value={`${group.title}-${idx}`} className="border-border px-6 last:border-b-0">
                    <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          <div id="contact" className="rounded-2xl border border-border bg-surface p-8 text-center">
            <h3 className="text-xl font-bold">Still have questions?</h3>
            <p className="mt-2 text-muted-foreground">
              Email{" "}
              <a href="mailto:support@shakatah.com" className="font-semibold text-primary hover:underline">
                support@shakatah.com
              </a>{" "}
              — we reply within 1 business day.
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default FAQ;
